import {
  getCountries,
  getCountryCallingCode,
  type CountryCode,
} from "libphonenumber-js";

export type PhoneCountry = {
  iso: CountryCode;
  dial: string;
  name: string;
};

/**
 * Flag image URL (flagcdn.com). Prefer over emoji — Windows Chrome often
 * renders regional-indicator pairs as bare letters (EG, AF) instead of flags.
 */
export function phoneCountryFlagSrc(
  iso: string,
  width: 20 | 40 | 80 = 40,
): string {
  const cc = iso.toLowerCase();
  if (!/^[a-z]{2}$/.test(cc)) return "";
  return `https://flagcdn.com/w${width}/${cc}.png`;
}

let constCached = new Map<string, PhoneCountry[]>();

/** All libphonenumber territories, sorted by localized name. */
export function getPhoneCountries(locale = "en"): PhoneCountry[] {
  const key = locale.startsWith("ar") ? "ar" : "en";
  const hit = constCached.get(key);
  if (hit) return hit;
  const names = new Intl.DisplayNames([key], { type: "region" });
  const list = getCountries()
    .map((iso) => {
      let name: string = iso;
      try {
        name = names.of(iso) ?? iso;
      } catch {
        /* non-ISO codes (e.g. AC) — keep iso */
      }
      return {
        iso,
        dial: `+${getCountryCallingCode(iso)}`,
        name,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name, key));
  constCached.set(key, list);
  return list;
}

export function findPhoneCountry(
  iso: string,
  locale = "en",
): PhoneCountry | undefined {
  return getPhoneCountries(locale).find((c) => c.iso === iso);
}

export function isPhoneCountryIso(value: string): value is CountryCode {
  return (getCountries() as string[]).includes(value);
}

/**
 * Guess calling-country from browser locale / timezone.
 * Falls back to Egypt (Xattir HQ) when unknown.
 */
export function detectPhoneCountryIso(): CountryCode {
  if (typeof navigator === "undefined") return "EG";

  const candidates: string[] = [];

  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? "";
    if (tz === "Africa/Cairo") candidates.push("EG");
    else if (tz.startsWith("Asia/Dubai") || tz === "Asia/Muscat")
      candidates.push("AE");
    else if (tz === "Asia/Riyadh") candidates.push("SA");
    else if (tz === "Asia/Kuwait") candidates.push("KW");
    else if (tz.startsWith("Europe/Berlin")) candidates.push("DE");
    else if (tz.startsWith("Europe/Copenhagen")) candidates.push("DK");
    else if (tz.startsWith("America/")) candidates.push("US");
    else if (tz.startsWith("Europe/London")) candidates.push("GB");
  } catch {
    /* ignore */
  }

  for (const lang of navigator.languages?.length
    ? navigator.languages
    : [navigator.language]) {
    const parts = String(lang).split(/[-_]/);
    if (parts.length >= 2) {
      candidates.push(parts[parts.length - 1]!.toUpperCase());
    }
  }

  for (const code of candidates) {
    if (isPhoneCountryIso(code)) return code;
  }
  return "EG";
}

export type ContactCurrency = "EGP" | "USD";

/** Egypt → EGP; every other country → USD. */
export function currencyForCountry(iso: string): ContactCurrency {
  return iso === "EG" ? "EGP" : "USD";
}
